import React, { useMemo, useState } from "react";
import { Form, FormikConfig, FormikProvider, useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectIsLoading } from "src/redux/auth/selectors";
import { registerAsync } from "src/redux/auth/actions";
import { Roles } from "src/@types/users";
import { IRegisterValues } from "src/@types/auth";
import { formatFormValuesByRole } from "./utils/formatFormValuesByRole";
import { ControlledFlow } from "./ControlledFlow";
import { Stepper } from "./Stepper";
import { SuccessStep } from "./Steps";
import { RegistrationFields } from "./types";
import { REGISTRATION_VALIDATION_SCHEMA } from "./constants/validation";
import {
  STEP_ITEMS_FOR_AGENT,
  STEP_ITEMS_FOR_OWNER,
} from "./constants/steps-by-role";

const INITIAL_VALUES = {
  [RegistrationFields.role]: Roles.OWNER,
  [RegistrationFields.country]: "",
  [RegistrationFields.companyName]: "",
  [RegistrationFields.companyAddress]: "",
  [RegistrationFields.website]: "",
  [RegistrationFields.firstName]: "",
  [RegistrationFields.lastName]: "",
  [RegistrationFields.description]: "",
  [RegistrationFields.industry]: "",
  [RegistrationFields.international]: true,
  [RegistrationFields.foundingDocs]: [],
  [RegistrationFields.reprecentionCountries]: [],
  [RegistrationFields.email]: "",
  [RegistrationFields.phoneNumber]: "",
  [RegistrationFields.password]: "",
};

export const RegistrationForm = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsLoading);

  const formikProps: FormikConfig<IRegisterValues> = {
    initialValues: INITIAL_VALUES,
    validationSchema: REGISTRATION_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const updatedValues = formatFormValuesByRole(values);
      dispatch(
        registerAsync({
          values: updatedValues,
          onSuccess: setSuccessStep,
        })
      );
    },
  };

  const formik = useFormik<IRegisterValues>(formikProps);

  const steps = useMemo(() => {
    const isAgent = formik.values.role === Roles.AGENT;
    return isAgent ? STEP_ITEMS_FOR_AGENT : STEP_ITEMS_FOR_OWNER;
  }, [formik.values.role]);

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === steps.length - 1;
  const isAccountCreated = activeStepIndex > steps.length - 1;

  const setSuccessStep = () => setActiveStepIndex(steps.length);
  const onNextClick = () => setActiveStepIndex((prev) => prev + 1);
  const onPrevClick = () => setActiveStepIndex((prev) => Math.max(0, prev - 1));

  return (
    <FormikProvider value={formik}>
      <Form className="flex flex-col gap-14">
        {!isAccountCreated && (
          <Stepper
            stepItems={steps}
            activeStepIndex={activeStepIndex}
            setActiveStepIndex={setActiveStepIndex}
          />
        )}

        <ControlledFlow activeStep={activeStepIndex}>
          {steps.map(({ stepComponent: StepComponent, id }) => (
            <StepComponent
              key={id}
              isLoading={isLoading}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
              onNextClick={onNextClick}
              onPrevClick={onPrevClick}
            />
          ))}
          <SuccessStep />
        </ControlledFlow>
      </Form>
    </FormikProvider>
  );
};
